import i18next from "@/i18n";
import { extractId } from "@repo/shopify/src/utils";
import { transformPhoneWithDash } from "@repo/utils/src/number";
import type { UserError } from "@shopify/hydrogen-react/storefront-api-types";
import React, { useEffect, useState } from "react";
import { Controller, type SubmitErrorHandler, type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Button from "@components/atoms/Button";
import Checkbox from "@components/atoms/Input/Checkbox";
import Input from "@components/atoms/Input/Input";
import Select from "@components/atoms/Select";

import useLayout from "@hooks/reducerHooks/useLayout";
import useMeta from "@hooks/reducerHooks/useMeta";
import useUser from "@hooks/reducerHooks/useUser";

import { NOTIFICATIONS } from "@utils/notifications";

import { fetchUserVerify } from "@api/lamdaApi";

const GENDER_OPTION = [
  { name: i18next.t("account.info.male"), value: i18next.t("account.info.male") },
  { name: i18next.t("account.info.female"), value: i18next.t("account.info.female") },
];

interface IFormData {
  firstName: string;
  lastName: string;
  phone: string;
  gender: string;
  birth: string;
  email: string;
  acceptsMarketing: boolean;
}

function Information() {
  const { setMetaTag } = useMeta();
  const { openToast } = useLayout();
  const { fetchUser, updateUser, accessToken, customer } = useUser();

  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [checkAcceptMarketing, setCheckAcceptMarketing] = useState(false);
  const [firstErrorKey, setFirstErrorKey] = useState("");

  useEffect(() => {
    setMetaTag({ title: `TUNE.KR -  PERSONAL INFORMATION` });
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<IFormData>({
    defaultValues: {
      firstName: customer.firstName || "",
      lastName: customer.lastName || "",
      phone: customer.phone ? transformPhoneWithDash(customer.phone) : "",
      gender: customer.gender?.value,
      birth: customer.birth?.value || "",
      email: customer.email || "",
      acceptsMarketing: customer.acceptsMarketing || false,
    },
  });

  /** 처음 고객 정보에 등록된 정보로 input value 입력 */
  useEffect(() => {
    reset({
      firstName: customer.firstName || "",
      lastName: customer.lastName || "",
      phone: customer.phone ? transformPhoneWithDash(customer.phone) : "",
      gender: customer.gender?.value,
      birth: customer.birth?.value,
      acceptsMarketing: customer.acceptsMarketing || false,
    });

    setCheckAcceptMarketing(customer.acceptsMarketing);
  }, [customer]);

  /** 업데이트 할 고객 정보 lambda api 호출 */
  const onSubmit: SubmitHandler<IFormData> = async (data) => {
    setIsLoading(true);

    const param = {
      phone: data.phone,
      firstName: data.firstName,
      lastName: data.lastName,
      birth: data.birth,
      gender: String(data.gender),
      acceptsMarketing: checkAcceptMarketing,
      customerToken: accessToken,
      customerId: extractId(customer.id),
    };

    const response = await fetchUserVerify(param);
    if (response) {
      if (response.data?.customerUpdate?.userErrors?.length) {
        response.data?.customerUpdate?.userErrors.forEach((error: UserError) => {
          openToast(error.message.includes("taken") ? NOTIFICATIONS.WARN_PHONE_VERIFY : NOTIFICATIONS.ERROR_DEFAULT);
        });
        return;
      }

      updateUser({ customer, accessToken, ...customer, ...param });
      await fetchUser({ variables: { customerAccessToken: accessToken } });
      openToast(NOTIFICATIONS.SUCCESS_MODIFY_ACCOUNT_INFO);
    }

    setIsLoading(false);
  };

  /** 취소 버튼 클릭시 초기 값으로 초기화 */
  const onCancel = () => {
    reset({
      firstName: customer.firstName ?? "",
      lastName: customer.lastName ?? "",
      phone: customer.phone ? transformPhoneWithDash(customer.phone) : "",
      gender: customer.gender?.value ?? "",
      birth: customer.birth?.value ?? "",
      acceptsMarketing: customer.acceptsMarketing || false,
    });

    setCheckAcceptMarketing(customer.acceptsMarketing);
  };

  const onError: SubmitErrorHandler<IFormData> = (error) => {
    const sortedKeys = Object.keys(getValues());
    sortedKeys.some((key) => {
      if (error[key as keyof typeof error]) {
        setFirstErrorKey(key);
        return true;
      }
      return false;
    });
  };

  const displayError = (key: string) => {
    return { hasError: firstErrorKey === key };
  };

  return (
    <form className="rounded-2.5 bg-tuneGrey-100 p-5" onSubmit={handleSubmit(onSubmit, onError)}>
      <span className="mb-5 block">{t("account.info.fix_me")}</span>
      <div className="mb-5">
        <div className="mb-5 flex flex-col gap-2.5">
          <div className="flex gap-2.5">
            <Input
              {...register("lastName")}
              {...displayError("lastName")}
              className="w-full"
              placeholder={t("account.shipping.address.last_name")}
              type="text"
              variant="white"
            />
            <Input
              placeholder={t("account.shipping.address.first_name")}
              type="text"
              variant="white"
              {...register("firstName")}
            />
          </div>
          <div>
            <Input
              {...register("email")}
              {...displayError("email")}
              className="text-secondary"
              name="email"
              readOnly
              type="text"
              variant="white"
            />
          </div>
          <div className="flex justify-between gap-2.5">
            <Input
              {...register("phone")}
              {...displayError("phone")}
              className="text-secondary"
              name="phone"
              placeholder={t("account.shipping.address.phone")}
              readOnly
              variant="white"
            />
            <Controller
              control={control}
              name="gender"
              render={({ field }) => (
                <Select
                  onChange={(e) => {
                    field.onChange(e.target.value === "-1" ? "성별" : e.target.value);
                  }}
                  options={GENDER_OPTION}
                  placeholder={t("account.shipping.address.sex")}
                  selected={field.value === "성별" ? { value: -1, name: "" } : field}
                  styleType="secondary"
                />
              )}
            />
          </div>
          <div>
            <Input
              {...register("birth")}
              className="text-secondary"
              maxLength={10}
              placeholder={t("account.shipping.address.birth_date")}
              readOnly
              type="tel"
              variant="white"
            />
          </div>
          {Object.values(errors).length ? (
            <span className="font-regular text-red-400">{Object.values(errors)[0].message}</span>
          ) : null}
        </div>
        <div className="w-fit">
          <span>
            <Checkbox
              checked={checkAcceptMarketing}
              label={t("account.info.marketing_agree")}
              onChange={(e) => {
                setCheckAcceptMarketing(e.target.checked);
              }}
            />
          </span>
        </div>
      </div>
      <div className="flex justify-center gap-2.5">
        <Button onClick={onCancel} styleType="secondary" variant="contained">
          {t("login.cancel")}
        </Button>
        <Button
          className="min-w-[99.52px]"
          isLoading={isLoading}
          onClick={handleSubmit(onSubmit, onError)}
          styleType="primary"
          type="submit"
          variant="contained"
        >
          {t("account.shipping.address.save")}
        </Button>
      </div>
    </form>
  );
}

export default Information;
